import React, { useCallback, useEffect } from 'react'
import FilterForm from '../components/FilterForm'
import JobListing from '../components/JobListing'
import NoActiveProjects from '../components/NoActiveProjects'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress, Grid, makeStyles, Typography } from '@material-ui/core'
import clsx from 'clsx'

import { PER_PAGE, RESULTS_WRAPPER_ID, sorts } from '../constants'
import * as actions from '../actionTypes'
import Pagination from '../components/project-board/Pagination'
import Sorting from '../components/project-board/Sorting'

const useStyles = makeStyles((theme) => ({
  paginationWrapper: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-end',
    },
  },
  listWrapper: {
    backgroundColor: `#EEEEEE`,
    padding: `1.5rem 0 5rem`,
    [theme.breakpoints.up('lg')]: {
      padding: `1.5rem 0 5rem`,
    },
  },
  loadingIndicatorWrapper: {
    alignItems: 'center',
    backgroundColor: 'rgba(256,256,256,0.75)',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    width: '100%',
    zIndex: 3,
  },
  resultsCount: {
    fontSize: theme.typography.pxToRem(14),
    fontStyle: 'italic',
    [theme.breakpoints.up('xs')]: {
      fontSize: '1rem',
    },
  },
  resultsHeading: {
    fontSize: theme.typography.pxToRem(24),
    [theme.breakpoints.up('xs')]: {
      fontSize: theme.typography.pxToRem(28),
    },
  },
  resultsWrapper: {
    margin: 'auto',
    minHeight: 300,
    padding: '0px 36px 6rem',
    [theme.breakpoints.up('lg')]: {
      maxWidth: '1640px',
    },
  },
  mobileFiltersOpen: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}))

const getResultsCount = ({ pageNumber, pageSize, totalResults }) => {
  const startingIndex = (pageNumber - 1) * PER_PAGE + 1
  const endIndex = startingIndex - 1 + pageSize
  return `${startingIndex} to ${endIndex} of ${totalResults}`
}

const ProjectBoardPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { viewFiltersStatus, activeProjects, isLoading, pagination, sortBy } = useSelector(
    (state) => state.projectBoard
  )

  const getProjects = useCallback(
    (pageNumber, sortBy, scrollToTop = false) => {
      dispatch({
        type: actions.PROJECT_BOARD_ACTIVE_PROJECTS_REQUEST,
        payload: { pageNumber, scrollToTop, sortBy: sortBy },
      })
    },
    [dispatch]
  )

  const handleSort = useCallback(
    (sortBy) => {
      dispatch({
        type: actions.PROJECT_BOARD_SET_SORT_BY,
        payload: { sortBy },
      })
      getProjects(1, sortBy)
    },
    [dispatch, getProjects]
  )

  useEffect(() => {
    getProjects(1)
  }, [getProjects])

  return (
    <div>
      {isLoading && (
        <div className={classes.loadingIndicatorWrapper}>
          <CircularProgress color="secondary" size={55} />
        </div>
      )}
      <Grid container>
        <Grid item xs={12}>
          <FilterForm />
        </Grid>
        <Grid
          className={clsx(classes.resultsWrapper, viewFiltersStatus && classes.mobileFiltersOpen)}
          container
          id={RESULTS_WRAPPER_ID}
          item
          xs={12}
        >
          {activeProjects.length > 0 && (
            <>
              <Grid container item xs={12} style={{ marginTop: 25 }}>
                <Grid item xs={6} style={{ alignItems: 'center', display: 'flex' }}>
                  <Typography className={classes.resultsHeading} color="primary" variant="h2">
                    Active Projects
                  </Typography>
                </Grid>
                <Grid container justify="flex-end" item xs={6}>
                  <Grid item xs="auto">
                    <Sorting
                      options={Object.values(sorts)}
                      onChange={handleSort}
                      placeholder="Sort By"
                      value={sortBy}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} style={{ marginTop: 25 }}>
                  <Typography className={classes.resultsCount} variant="body1">
                    Showing Results {getResultsCount(pagination)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid className={classes.listWrapper} item xs={12}>
                {activeProjects.map((project) => (
                  <JobListing
                    job={{
                      id: project.id,
                      title: project.topic,
                      date: project.creationDatetime,
                      description: project.projectDescription,
                      type: project.positionType,
                      dept: project.department,
                      topic: project.division,
                      attending: project.attending,
                    }}
                    key={project.id}
                  />
                ))}
              </Grid>
              {pagination.totalPages > 1 && (
                <Grid className={classes.paginationWrapper} container item xs={12}>
                  <Pagination
                    onPageRequest={(page) => getProjects(page, sortBy, true)}
                    pageNumber={pagination.pageNumber}
                    totalPages={pagination.totalPages}
                  />
                </Grid>
              )}
            </>
          )}
          {!isLoading && activeProjects.length === 0 && <NoActiveProjects />}
        </Grid>
      </Grid>
    </div>
  )
}

export default ProjectBoardPage
